import React from 'react';

import { cn } from '@/lib/utils';
import { getPlatformIcon, getPlatformLabel, sortPlatforms } from '@/utils/platformUtils';

export type PlatformType =
  'facebook' | 'instagram' | 'twitter' | 'linkedin' | 'tiktok' | 'shopify' | string;

export interface PlatformOption {
  id: string;
  label?: string;
  platform?: string;
  /** Switcher mode: render an amber health dot on this pill (needs reconnect). */
  needsReconnect?: boolean;
}

interface PlatformIconBaseProps {
  className?: string;
}

interface PlatformIconSingleProps extends PlatformIconBaseProps {
  mode?: 'single';
  platform: PlatformType;
  size?: 'sm' | 'md' | 'lg' | number;
}

interface PlatformIconListProps extends PlatformIconBaseProps {
  mode: 'list';
  platforms: string[];
  maxVisible?: number;
  itemClassName?: string;
  iconClassName?: string;
}

interface PlatformIconSwitcherProps extends PlatformIconBaseProps {
  mode: 'switcher';
  platforms: PlatformOption[];
  activeId: string;
  onSelect: (id: string) => void;
  size?: 'sm' | 'md';
}

export type PlatformIconProps =
  PlatformIconSingleProps | PlatformIconListProps | PlatformIconSwitcherProps;

export const PlatformIcon: React.FC<PlatformIconProps> = (props) => {
  const { mode = 'single', className } = props as any;

  if (mode === 'single') {
    const { platform, size = 'md' } = props as PlatformIconSingleProps;
    const platformKey = platform.toLowerCase().trim();
    const iconSize = typeof size === 'number' ? size : { sm: 12, md: 14, lg: 18 }[size];
    const IconCmp = getPlatformIcon(platformKey);

    return (
      <span
        className={cn('inline-flex shrink-0 items-center justify-center', className)}
        style={{ width: iconSize, height: iconSize }}
      >
        <IconCmp className="h-full w-full" aria-hidden="true" />
      </span>
    );
  }

  if (mode === 'list') {
    const {
      platforms,
      maxVisible = 4,
      itemClassName,
      iconClassName,
    } = props as PlatformIconListProps;
    const uniquePlatforms = Array.from(new Set(sortPlatforms(platforms)));
    if (uniquePlatforms.length === 0) return null;

    const visiblePlatforms = uniquePlatforms.slice(0, maxVisible);

    return (
      <div className={cn('flex items-center gap-1', className)}>
        {visiblePlatforms.map((platform) => {
          const IconCmp = getPlatformIcon(platform as PlatformType);
          return (
            <span
              key={platform}
              className={cn('flex size-5 items-center justify-center', itemClassName)}
              title={getPlatformLabel(platform)}
            >
              <IconCmp className={cn('size-3.5', iconClassName)} />
            </span>
          );
        })}
      </div>
    );
  }

  if (mode === 'switcher') {
    const { platforms, activeId, onSelect, size = 'sm' } = props as PlatformIconSwitcherProps;
    const itemClass = size === 'sm' ? 'h-6 px-2.5 text-xs' : 'h-8 px-4 text-xs';

    return (
      <div
        className={cn('inline-flex items-center gap-0.5 rounded-full bg-muted p-0.5', className)}
      >
        {platforms.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => onSelect(p.id)}
            title={p.needsReconnect ? `${p.label ?? p.platform} needs reconnecting` : undefined}
            className={cn(
              'relative flex items-center justify-center rounded-full font-medium transition',
              itemClass,
              activeId === p.id
                ? cn('bg-background shadow-sm', p.platform ? undefined : 'text-foreground')
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {p.platform ? (
              <PlatformIcon platform={p.platform as PlatformType} size="sm" />
            ) : (
              p.label
            )}
            {p.needsReconnect && (
              <span
                aria-hidden
                className="absolute right-0.5 top-0.5 size-1.5 rounded-full bg-warning ring-1 ring-background"
              />
            )}
          </button>
        ))}
      </div>
    );
  }

  return null;
};

export const SOCIAL_PLATFORMS: PlatformOption[] = [
  { id: 'all', label: 'All' },
  { id: 'instagram', platform: 'instagram' },
  { id: 'facebook', platform: 'facebook' },
  { id: 'linkedin', platform: 'linkedin' },
  { id: 'tiktok', platform: 'tiktok' },
  { id: 'youtube', platform: 'youtube' },
];

export const ANALYTICS_PLATFORMS: PlatformOption[] = [
  ...SOCIAL_PLATFORMS,
  { id: 'shopify', platform: 'shopify' },
];
